import axios from "axios";

// The Laravel backend (backendv2). Falls back to the local dev default so the
// app boots even before .env is copied.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

// The module API lives under /api. Auth (login/logout) and the Sanctum CSRF
// cookie live at the root, OUTSIDE /api — see `root` below.
export const API = `${BACKEND_URL}/api`;

/*
 * Auth is Sanctum SPA cookie mode (docs/laravel-rewrite/01-laravel-setup-and-auth.md):
 * a session cookie plus an XSRF-TOKEN cookie that axios echoes back as the
 * X-XSRF-TOKEN header. There is NO bearer token — the Python original kept a JWT
 * in localStorage, which is XSS-exposed by construction. `withCredentials` is what
 * makes the browser send (and axios echo) those cookies cross-port.
 */
// withXSRFToken is required on axios 1.x: the SPA (:3000 / SPA subdomain) and the
// API (:8000 / api subdomain) are different origins, and axios only echoes the
// XSRF-TOKEN cookie as the X-XSRF-TOKEN header same-origin unless this is set.
// Without it, cross-origin state-changing calls fail Laravel's CSRF check (419).
export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  withXSRFToken: true,
});

// Root-level endpoints: GET /sanctum/csrf-cookie, POST /login, POST /logout.
export const root = axios.create({
  baseURL: BACKEND_URL,
  withCredentials: true,
  withXSRFToken: true,
});

// Prime the XSRF-TOKEN cookie. Call once before the first state-changing
// request of a session (i.e. before login). Plain GETs don't need it.
export async function bootstrapCsrf() {
  await root.get("/sanctum/csrf-cookie");
}

/*
 * Legacy bearer-token helpers. Sanctum cookie auth doesn't use them, but a
 * couple of not-yet-ported pages (Reports, onboarding AttachmentsPanel) still
 * import getToken for the old JWT-in-query download pattern (audit C5). Kept as
 * shims so those imports don't break the build; they go live again only if a
 * token flow is ever reintroduced.
 */
const TOKEN_KEY = "hah_token";
export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(t) {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
}

// A 401 anywhere means the session lapsed — bounce to login (unless already there).
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401 && window.location.pathname !== "/login") {
      window.location.href = "/login";
    }
    return Promise.reject(err);
  }
);

/*
 * Laravel error shapes:
 *   422 → { message, errors: { field: ["msg", ...] } }
 *   4xx/5xx → { message }
 * The old FastAPI `detail` shape is kept as a fallback for endpoints not yet
 * ported to Laravel.
 */
export function formatApiError(err) {
  const data = err?.response?.data;

  if (data?.errors && typeof data.errors === "object") {
    const msgs = Object.values(data.errors).flat().filter(Boolean);
    if (msgs.length) return msgs.join("; ");
  }
  if (typeof data?.message === "string" && data.message) return data.message;

  const detail = data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join("; ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;

  return err?.message || "Something went wrong.";
}
